import { Controller, type UseControllerProps } from 'react-hook-form';
import { useObjectTranslation } from '../Inputs/hooks';
import { PhoneInput, type Props } from '../Inputs/PhoneInput/PhoneInput';
import { useRules } from './hooks';
import type { RequiredField } from './types';

/**
 * PhoneFieldProps
 * @public
 */
export type PhoneFieldProps = Props & UseControllerProps & RequiredField;

/**
 * PhoneField component
 *
 * @example
 * ```tsx
 * <Form>
 *   <PhoneField name="phone" />
 * </Form>
 * ```
 *
 * @public
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param defaultValue - default value
 * @param control - form control
 * @param required - is field required
 * @param restProps - props
 */
export const PhoneField: React.FC<PhoneFieldProps> = ({
  rules,
  shouldUnregister,
  defaultValue,
  control,
  required,
  ...restProps
}) => {
  const innerRules = useRules({ required, ...rules });
  const { name } = restProps;
  const { t } = useObjectTranslation('common');

  return (
    <Controller
      rules={innerRules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, value, onChange, ...fieldProps } = field;
        const undecoratedValue = value ? value.replace(/^\+/, '') : '';
        const decoratedOnChange = (v: string) => {
          // react-phone-input-2 sends only country code when field is empty
          // (e.g. '48' for Poland) — treat as null to avoid false validation
          const digits = v.replace(/\D/g, '');
          onChange(digits.length > 3 ? `+${v}` : null);
        };
        const error = fieldState.error?.message;

        return (
          <PhoneInput
            ref={ref}
            value={undecoratedValue}
            onChange={decoratedOnChange}
            error={error ? t(error) : undefined}
            {...fieldProps}
            {...restProps}
          />
        );
      }}
    />
  );
};
